import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaImage, FaVideo, FaLock, FaHistory, FaCalendarAlt, FaComment, FaEye, FaEyeSlash, FaCopy } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import API_BASE_URL from '../config';
import TrackManager from './TrackManager';
import PhotoManager from './PhotoManager';
import VideoManager from './VideoManager';
import BackupManager from './BackupManager';
import BookingManager from './BookingManager';
import ChatManager from './ChatManager';
import './Admin.css';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 минут в миллисекундах
const AUTH_STORAGE_KEY = 'admin_token';
const LAST_ACTIVITY_KEY = 'admin_last_activity';

const Admin = ({ onTrackAdded }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordFieldErrors, setChangePasswordFieldErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [recoverySecret, setRecoverySecret] = useState('');
  const [recoverySecretConfirm, setRecoverySecretConfirm] = useState('');
  const [recoverySecretError, setRecoverySecretError] = useState('');
  const [recoverySecretSuccess, setRecoverySecretSuccess] = useState(false);
  const [recoverySecretLoading, setRecoverySecretLoading] = useState(false);
  const [recoverySecretPassword, setRecoverySecretPassword] = useState('');
  const [recoverySecretFieldErrors, setRecoverySecretFieldErrors] = useState({
    currentPassword: '',
    secret: '',
    confirmSecret: ''
  });
  const [savedRecoverySecret, setSavedRecoverySecret] = useState(() => {
    // Загружаем сохраненный recovery secret из localStorage при инициализации
    return localStorage.getItem('admin_recovery_secret') || '';
  });
  const [showRecoverySecret, setShowRecoverySecret] = useState(false);
  
  // Состояния для показа/скрытия паролей
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
  const [showRecoverySecretPassword, setShowRecoverySecretPassword] = useState(false);
  
  // Восстанавливаем активную вкладку из localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('admin_active_tab');
    return savedTab || 'tracks';
  });
  const [unreadBookingsCount, setUnreadBookingsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Настройка axios interceptor для автоматической установки токена
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Публичные endpoints, которые не требуют токена
        const publicEndpoints = [
          '/api/admin/login',
          '/api/admin/reset-password-by-secret'
        ];
        
        const isPublicEndpoint = publicEndpoints.some(endpoint => 
          config.url?.includes(endpoint)
        );
        
        // Добавляем токен только для защищенных endpoints
        if (!isPublicEndpoint) {
          const token = localStorage.getItem(AUTH_STORAGE_KEY);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('🔑 Token added to request:', config.url, 'Token exists:', !!token);
          } else {
            console.warn('⚠️ No token found for protected request:', config.url);
          }
        }
        
        // Ensure withCredentials is set for CORS
        if (!config.withCredentials) {
          config.withCredentials = true;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor для обработки 401 ошибок (исключая логин)
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Не обрабатываем 401 для логина - это нормальная ошибка при неверном пароле
        if (error.response?.status === 401 && 
            error.config?.url?.includes('/api/admin') && 
            !error.config?.url?.includes('/api/admin/login')) {
          console.error('❌ 401 Unauthorized - clearing auth');
          localStorage.removeItem(AUTH_STORAGE_KEY);
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Функция для сохранения авторизации
  const saveAuth = (token) => {
    console.log('💾 Saving auth token, length:', token?.length);
    localStorage.setItem(AUTH_STORAGE_KEY, token);
    // Set default axios authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('✅ Token saved and set in axios defaults');
    updateLastActivity();
  };

  // Функция для очистки авторизации
  const clearAuth = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // Обновление времени последней активности
  const updateLastActivity = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  };

  // Запуск таймера простоя
  const startIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      console.log('Idle timeout reached, logging out...');
      clearAuth();
      alert(t('admin.sessionExpired') || 'Сессия истекла из-за неактивности. Пожалуйста, войдите снова.');
    }, IDLE_TIMEOUT);
  }, [t, clearAuth]);

  // Функция для получения счетчиков непрочитанных
  const fetchUnreadCounts = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('❌ fetchUnreadCounts: not authenticated');
      return;
    }
    
    try {
      console.log('🔄 Fetching unread counts...');
      const [bookingsResponse, messagesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/bookings/unread-count`),
        axios.get(`${API_BASE_URL}/api/chat/messages/unread-count`)
      ]);
      
      const bookingsCount = bookingsResponse.data?.count || 0;
      const messagesCount = messagesResponse.data?.count || 0;
      
      console.log(`📊 API Response - Bookings: ${bookingsCount}, Messages: ${messagesCount}`);
      
      setUnreadBookingsCount(bookingsCount);
      setUnreadMessagesCount(messagesCount);
      
      // Логирование для отладки
      if (bookingsCount > 0 || messagesCount > 0) {
        console.log(`✅ Unread counts updated: Bookings: ${bookingsCount}, Messages: ${messagesCount}`);
        console.log(`🎨 Classes should be applied: bookings has-unread=${bookingsCount > 0}, messages has-unread=${messagesCount > 0}`);
      }
    } catch (error) {
      console.error('❌ Error fetching unread counts:', error);
    }
  }, [isAuthenticated]);

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = () => {
      const savedToken = localStorage.getItem(AUTH_STORAGE_KEY);
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
      
      if (savedToken && lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
        
        // Если прошло меньше времени простоя, восстанавливаем авторизацию
        if (timeSinceLastActivity < IDLE_TIMEOUT) {
          setIsAuthenticated(true);
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          lastActivityRef.current = parseInt(lastActivity, 10);
          startIdleTimer();
        } else {
          // Время простоя истекло, очищаем авторизацию
          clearAuth();
        }
      }
    };

    checkAuth();
  }, [startIdleTimer, clearAuth]);

  // Загрузка сохраненного recovery secret при монтировании
  useEffect(() => {
    const savedSecret = localStorage.getItem('admin_recovery_secret');
    if (savedSecret) {
      setSavedRecoverySecret(savedSecret);
    }
  }, []);

  // Обновление счетчиков при авторизации и периодически
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadBookingsCount(0);
      setUnreadMessagesCount(0);
      return;
    }

    fetchUnreadCounts();
    
    // Обновляем счетчики каждые 10 секунд для более быстрой реакции
    const interval = setInterval(fetchUnreadCounts, 10000);
    
    // Слушаем события обновления от дочерних компонентов
    const handleUpdateCounts = () => {
      console.log('🔔 Event received: admin:updateCounts');
      fetchUnreadCounts();
    };
    
    window.addEventListener('admin:updateCounts', handleUpdateCounts);
    
    // Обновляем счетчики при возврате фокуса на окно
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        console.log('👁️ Visibility changed: visible');
        fetchUnreadCounts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Обновляем счетчики при фокусе на окно
    const handleFocus = () => {
      if (isAuthenticated) {
        console.log('🎯 Window focused');
        fetchUnreadCounts();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('admin:updateCounts', handleUpdateCounts);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, fetchUnreadCounts]);

  // Отладка: отслеживание изменений счетчиков
  useEffect(() => {
    console.log(`🔍 State changed - Bookings: ${unreadBookingsCount}, Messages: ${unreadMessagesCount}`);
    console.log(`🎨 Should apply classes - Bookings has-unread: ${unreadBookingsCount > 0}, Messages has-unread: ${unreadMessagesCount > 0}`);
  }, [unreadBookingsCount, unreadMessagesCount]);

  // Отслеживание активности пользователя
  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateLastActivity();
      startIdleTimer();
    };

    // Добавляем обработчики событий
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Запускаем таймер при монтировании
    startIdleTimer();

    // Очистка при размонтировании
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [isAuthenticated, startIdleTimer]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setPasswordError(''); // Сбрасываем ошибку при новой попытке
    
    if (!password.trim()) {
      setPasswordError(t('admin.enterPassword') || 'Please enter your password');
      return;
    }
    
    try {
      console.log('🔐 Attempting login...');
      const response = await axios.post(`${API_BASE_URL}/api/admin/login`, {
        password: password.trim()
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-skip-rate-limit': process.env.NODE_ENV === 'development' ? 'true' : 'false'
        },
        withCredentials: true // Include credentials for CORS
      });
      console.log('✅ Login response:', response.data);
      
      if (response.data && response.data.success && response.data.token) {
        setIsAuthenticated(true);
        saveAuth(response.data.token);
        setPassword('');
        setPasswordError('');
        console.log('✅ Login successful!');
      } else {
        setPasswordError(t('admin.invalidPassword') || 'Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      
      // Check for CORS errors specifically
      if (error.message && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.error('🚫 CORS Error detected:', error.message);
        setPasswordError(t('admin.connectionError') || 'Connection error. Please check your internet connection and try again.');
        return;
      }
      
      if (error.response) {
        // Сервер ответил с ошибкой
        const errorMessage = error.response.data?.error || error.response.data?.message;
        if (error.response.status === 401) {
          setPasswordError(t('admin.invalidPassword') || 'Invalid password. Please try again.');
        } else if (error.response.status === 429) {
          setPasswordError(errorMessage || 'Too many login attempts. Please try again later.');
        } else {
          setPasswordError(errorMessage || t('admin.invalidPassword') || 'Invalid password. Please try again.');
        }
      } else if (error.request) {
        // Запрос был отправлен, но ответа не получено
        setPasswordError(t('admin.connectionError') || 'Connection error. Please check your internet connection and try again.');
        console.error('Request error:', error.request);
      } else {
        // Что-то другое
        setPasswordError(t('admin.invalidPassword') || 'Invalid password. Please try again.');
      }
    }
  };

  const validateChangePassword = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    let hasErrors = false;

    if (!currentPassword.trim()) {
      errors.currentPassword = t('admin.currentPasswordRequired');
      hasErrors = true;
    }

    if (!newPassword.trim()) {
      errors.newPassword = t('admin.newPasswordRequired');
      hasErrors = true;
    } else if (newPassword.length < 6) {
      errors.newPassword = t('admin.passwordMinLength');
      hasErrors = true;
    }

    if (!newPasswordConfirm.trim()) {
      errors.confirmPassword = t('admin.confirmPasswordRequired');
      hasErrors = true;
    } else if (newPassword !== newPasswordConfirm) {
      errors.confirmPassword = t('admin.passwordsDoNotMatch');
      hasErrors = true;
    }

    setChangePasswordFieldErrors(errors);
    return !hasErrors;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess(false);
    setChangePasswordFieldErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });

    if (!validateChangePassword()) {
      return;
    }

    setChangePasswordLoading(true);

    try {
      // Получаем токен и проверяем его наличие
      const token = localStorage.getItem(AUTH_STORAGE_KEY);
      console.log('🔍 Change password - Token exists:', !!token, 'Token length:', token?.length);
      
      if (!token) {
        console.error('❌ No token found - user needs to login again');
        setChangePasswordError(t('admin.invalidPassword') || 'Необходима авторизация. Пожалуйста, войдите снова.');
        setChangePasswordLoading(false);
        setIsAuthenticated(false);
        clearAuth();
        return;
      }

      // Убеждаемся, что токен установлен в axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('✅ Token set in axios defaults');

      // Токен будет автоматически добавлен через interceptor
      console.log('📤 Sending change password request...');
      const response = await axios.post(`${API_BASE_URL}/api/admin/change-password`, {
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim()
      });
      console.log('✅ Change password response:', response.data);
      
      if (response.data && response.data.success) {
        setChangePasswordSuccess(true);
        setChangePasswordError('');
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
        // Clear success message after 5 seconds
        setTimeout(() => setChangePasswordSuccess(false), 5000);
      } else {
        const errorMsg = response.data?.error || t('admin.changePasswordError');
        setChangePasswordError(errorMsg);
        if (errorMsg.includes('Current password') || errorMsg.includes('Текущий пароль') || errorMsg.includes('incorrect') || errorMsg.includes('неверен')) {
          setChangePasswordFieldErrors(prev => ({ ...prev, currentPassword: t('admin.currentPasswordIncorrect') }));
        }
      }
    } catch (error) {
      console.error('Change password error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Если токен недействителен, разлогиниваем пользователя
      if (error.response?.status === 401) {
        setChangePasswordError(t('admin.invalidPassword') || 'Сессия истекла. Пожалуйста, войдите снова.');
        setIsAuthenticated(false);
        clearAuth();
        return;
      }
      
      // Обработка ошибок валидации (400)
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error || t('admin.changePasswordError');
        setChangePasswordError(errorMsg);
        
        // Проверяем тип ошибки и устанавливаем соответствующие сообщения
        if (errorMsg.includes('Current password') || errorMsg.includes('Текущий пароль') || errorMsg.includes('incorrect') || errorMsg.includes('неверен')) {
          setChangePasswordFieldErrors(prev => ({ ...prev, currentPassword: t('admin.currentPasswordIncorrect') || 'Current password is incorrect' }));
        } else if (errorMsg.includes('at least 6 characters') || errorMsg.includes('минимум 6 символов')) {
          setChangePasswordFieldErrors(prev => ({ ...prev, newPassword: t('admin.passwordMinLength') || 'Password must be at least 6 characters' }));
        } else {
          setChangePasswordError(errorMsg);
        }
      } else {
        // Общая ошибка
        const errorMsg = error.response?.data?.error || error.message || t('admin.changePasswordError') || 'Failed to change password. Please try again.';
        setChangePasswordError(errorMsg);
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const validateRecoverySecret = () => {
    const errors = {
      currentPassword: '',
      secret: '',
      confirmSecret: ''
    };
    let hasErrors = false;

    if (!recoverySecretPassword.trim()) {
      errors.currentPassword = t('admin.currentPasswordRequired');
      hasErrors = true;
    }

    if (!recoverySecret.trim()) {
      errors.secret = t('admin.secretRequired');
      hasErrors = true;
    } else if (recoverySecret.length < 8) {
      errors.secret = t('admin.secretMinLength');
      hasErrors = true;
    }

    if (!recoverySecretConfirm.trim()) {
      errors.confirmSecret = t('admin.confirmSecretRequired');
      hasErrors = true;
    } else if (recoverySecret !== recoverySecretConfirm) {
      errors.confirmSecret = t('admin.secretsDoNotMatch');
      hasErrors = true;
    }

    setRecoverySecretFieldErrors(errors);
    return !hasErrors;
  };

  const handleUpdateRecoverySecret = async (e) => {
    e.preventDefault();
    setRecoverySecretError('');
    setRecoverySecretSuccess(false);
    setRecoverySecretFieldErrors({ currentPassword: '', secret: '', confirmSecret: '' });

    if (!validateRecoverySecret()) {
      return;
    }

    setRecoverySecretLoading(true);

    try {
      // Получаем токен и проверяем его наличие
      const token = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!token) {
        setRecoverySecretError(t('admin.invalidPassword') || 'Необходима авторизация. Пожалуйста, войдите снова.');
        setRecoverySecretLoading(false);
        setIsAuthenticated(false);
        clearAuth();
        return;
      }

      // Токен будет автоматически добавлен через interceptor
      const response = await axios.post(`${API_BASE_URL}/api/admin/update-recovery-secret`, {
        currentPassword: recoverySecretPassword.trim(),
        newSecret: recoverySecret.trim()
      });
      
      if (response.data && response.data.success) {
        setRecoverySecretSuccess(true);
        setRecoverySecretError('');
        // Сохраняем секретный код для отображения
        const secretToSave = recoverySecret.trim();
        localStorage.setItem('admin_recovery_secret', secretToSave);
        setSavedRecoverySecret(secretToSave);
        setRecoverySecretPassword('');
        setRecoverySecret('');
        setRecoverySecretConfirm('');
        // Clear success message after 5 seconds
        setTimeout(() => setRecoverySecretSuccess(false), 5000);
      } else {
        const errorMsg = response.data?.error || t('admin.updateRecoverySecretError');
        setRecoverySecretError(errorMsg);
        if (errorMsg.includes('Current password') || errorMsg.includes('Текущий пароль') || errorMsg.includes('incorrect') || errorMsg.includes('неверен')) {
          setRecoverySecretFieldErrors(prev => ({ ...prev, currentPassword: t('admin.currentPasswordIncorrect') }));
        }
      }
    } catch (error) {
      console.error('Update recovery secret error:', error);
      
      // Если токен недействителен, разлогиниваем пользователя
      if (error.response?.status === 401) {
        setRecoverySecretError(t('admin.invalidPassword') || 'Сессия истекла. Пожалуйста, войдите снова.');
        setIsAuthenticated(false);
        clearAuth();
        return;
      }
      
      const errorMsg = error.response?.data?.error || t('admin.updateRecoverySecretError');
      setRecoverySecretError(errorMsg);
      if (errorMsg.includes('Current password') || errorMsg.includes('Текущий пароль') || errorMsg.includes('incorrect') || errorMsg.includes('неверен')) {
        setRecoverySecretFieldErrors(prev => ({ ...prev, currentPassword: t('admin.currentPasswordIncorrect') }));
      }
    } finally {
      setRecoverySecretLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <motion.div
          className="login-form"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring" }}
        >
          <FaLock className="lock-icon" />
          <h2>Admin Login</h2>
          <form onSubmit={handleLogin}>
            {/* Hidden username field for accessibility */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value="admin"
              readOnly
              tabIndex={-1}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none'
              }}
              aria-hidden="true"
            />
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(''); // Сбрасываем ошибку при вводе
                }}
                className={passwordError ? 'password-input-error' : ''}
                autoComplete="current-password"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-btn"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
              {passwordError && (
                <span className="password-error-message">{passwordError}</span>
              )}
            </div>
            <button type="submit">Login</button>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/admin/reset-password')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  textDecoration: 'underline',
                  padding: '0.5rem'
                }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <motion.div
        className="admin-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="admin-tabs">
          <button
            className={activeTab === 'tracks' ? 'active' : ''}
            onClick={() => {
              setActiveTab('tracks');
              localStorage.setItem('admin_active_tab', 'tracks');
            }}
          >
            {t('admin.tracks')}
          </button>
          <button
            className={activeTab === 'photos' ? 'active' : ''}
            onClick={() => {
              setActiveTab('photos');
              localStorage.setItem('admin_active_tab', 'photos');
            }}
          >
            <FaImage /> {t('admin.photos')}
          </button>
          <button
            className={activeTab === 'videos' ? 'active' : ''}
            onClick={() => {
              setActiveTab('videos');
              localStorage.setItem('admin_active_tab', 'videos');
            }}
          >
            <FaVideo /> {t('admin.videos')}
          </button>
          <button
            className={[
              activeTab === 'bookings' ? 'active' : '',
              unreadBookingsCount > 0 ? 'has-unread' : ''
            ].filter(Boolean).join(' ')}
            onClick={() => {
              setActiveTab('bookings');
              localStorage.setItem('admin_active_tab', 'bookings');
              fetchUnreadCounts(); // Обновляем счетчики при переключении
            }}
            title={unreadBookingsCount > 0 ? `${unreadBookingsCount} непрочитанных бронирований` : ''}
            data-unread-count={unreadBookingsCount}
          >
            <FaCalendarAlt /> {t('admin.bookings')}
            {unreadBookingsCount > 0 && (
              <span className="admin-tab-badge">{unreadBookingsCount}</span>
            )}
          </button>
          <button
            className={[
              activeTab === 'chat' ? 'active' : '',
              unreadMessagesCount > 0 ? 'has-unread' : ''
            ].filter(Boolean).join(' ')}
            onClick={() => {
              setActiveTab('chat');
              localStorage.setItem('admin_active_tab', 'chat');
              fetchUnreadCounts(); // Обновляем счетчики при переключении
            }}
            title={unreadMessagesCount > 0 ? `${unreadMessagesCount} непрочитанных сообщений` : ''}
            data-unread-count={unreadMessagesCount}
          >
            <FaComment /> {t('admin.messages')}
            {unreadMessagesCount > 0 && (
              <span className="admin-tab-badge">{unreadMessagesCount}</span>
            )}
          </button>
          <button
            className={activeTab === 'backups' ? 'active' : ''}
            onClick={() => {
              setActiveTab('backups');
              localStorage.setItem('admin_active_tab', 'backups');
            }}
          >
            <FaHistory /> {t('admin.backups')}
          </button>
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => {
              setActiveTab('settings');
              localStorage.setItem('admin_active_tab', 'settings');
            }}
            style={{ marginLeft: 'auto' }}
          >
            <FaLock /> {t('admin.settings')}
          </button>
        </div>

        {activeTab === 'tracks' && <TrackManager onTrackAdded={onTrackAdded} />}
        {activeTab === 'photos' && <PhotoManager />}
        {activeTab === 'videos' && <VideoManager />}

        {activeTab === 'backups' && <BackupManager />}
        {activeTab === 'bookings' && <BookingManager />}
        {activeTab === 'chat' && <ChatManager />}
        
        {activeTab === 'settings' && (
          <div style={{ 
            padding: '2rem', 
            maxWidth: '600px', 
            margin: '0 auto',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Change Password Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                marginBottom: '8px', 
                color: '#fff', 
                fontSize: '1.4rem',
                fontWeight: '600'
              }}>
                {t('admin.changePassword')}
              </h3>
              <p style={{ 
                marginBottom: '20px', 
                fontSize: '0.9rem', 
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.5'
              }}>
                {t('admin.changePasswordDescription')}
              </p>
              
              {changePasswordSuccess && (
                <div style={{ 
                  color: '#4facfe', 
                  fontSize: '0.9rem', 
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(79, 172, 254, 0.1)',
                  border: '1px solid rgba(79, 172, 254, 0.3)',
                  borderRadius: '8px'
                }}>
                  ✓ {t('admin.passwordUpdatedSuccess')}
                </div>
              )}

              <form onSubmit={handleChangePassword}>
                {/* Hidden username field for accessibility */}
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value="admin"
                  readOnly
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                  }}
                  aria-hidden="true"
                />
                <div className="password-input-wrapper" style={{ marginBottom: '16px', position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder={t('admin.currentPassword')}
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setChangePasswordError('');
                      setChangePasswordFieldErrors(prev => ({ ...prev, currentPassword: '' }));
                    }}
                    className={changePasswordFieldErrors.currentPassword ? 'password-input-error' : ''}
                    autoComplete="current-password"
                    disabled={changePasswordLoading}
                    style={{
                      width: '100%',
                      padding: '12px 45px 12px 16px',
                      border: changePasswordFieldErrors.currentPassword ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="password-toggle-btn"
                    tabIndex={-1}
                    disabled={changePasswordLoading}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.6)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem',
                      opacity: changePasswordLoading ? 0.5 : 1
                    }}
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {changePasswordFieldErrors.currentPassword && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {changePasswordFieldErrors.currentPassword}
                    </span>
                  )}
                </div>

                <div className="password-input-wrapper" style={{ marginBottom: '16px', position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder={t('admin.newPassword')}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setChangePasswordError('');
                      setChangePasswordFieldErrors(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
                    }}
                    className={changePasswordFieldErrors.newPassword ? 'password-input-error' : ''}
                    autoComplete="new-password"
                    disabled={changePasswordLoading}
                    style={{
                      width: '100%',
                      padding: '12px 45px 12px 16px',
                      border: changePasswordFieldErrors.newPassword ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="password-toggle-btn"
                    tabIndex={-1}
                    disabled={changePasswordLoading}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.6)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem',
                      opacity: changePasswordLoading ? 0.5 : 1
                    }}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {changePasswordFieldErrors.newPassword && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {changePasswordFieldErrors.newPassword}
                    </span>
                  )}
                </div>

                <div className="password-input-wrapper" style={{ marginBottom: '20px', position: 'relative' }}>
                  <input
                    type={showNewPasswordConfirm ? 'text' : 'password'}
                    placeholder={t('admin.confirmNewPassword')}
                    value={newPasswordConfirm}
                    onChange={(e) => {
                      setNewPasswordConfirm(e.target.value);
                      setChangePasswordError('');
                      setChangePasswordFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                    }}
                    className={changePasswordFieldErrors.confirmPassword ? 'password-input-error' : ''}
                    autoComplete="new-password"
                    disabled={changePasswordLoading}
                    style={{
                      width: '100%',
                      padding: '12px 45px 12px 16px',
                      border: changePasswordFieldErrors.confirmPassword ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPasswordConfirm(!showNewPasswordConfirm)}
                    className="password-toggle-btn"
                    tabIndex={-1}
                    disabled={changePasswordLoading}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.6)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem',
                      opacity: changePasswordLoading ? 0.5 : 1
                    }}
                  >
                    {showNewPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {changePasswordFieldErrors.confirmPassword && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {changePasswordFieldErrors.confirmPassword}
                    </span>
                  )}
                </div>

                {changePasswordError && !changePasswordFieldErrors.currentPassword && !changePasswordFieldErrors.newPassword && !changePasswordFieldErrors.confirmPassword && (
                  <div style={{ 
                    color: '#e53e3e', 
                    fontSize: '0.85rem', 
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'rgba(229, 62, 62, 0.1)',
                    border: '1px solid rgba(229, 62, 62, 0.3)',
                    borderRadius: '8px'
                  }}>
                    {changePasswordError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={changePasswordLoading || changePasswordSuccess}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: changePasswordLoading || changePasswordSuccess 
                      ? 'rgba(74, 85, 104, 0.5)' 
                      : 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: changePasswordLoading || changePasswordSuccess ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: changePasswordLoading || changePasswordSuccess ? 0.7 : 1,
                    boxShadow: changePasswordLoading || changePasswordSuccess ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!changePasswordLoading && !changePasswordSuccess) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(74, 85, 104, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!changePasswordLoading && !changePasswordSuccess) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                    }
                  }}
                >
                  {changePasswordLoading ? t('admin.updating') : t('admin.updatePassword')}
                </button>
              </form>
            </div>

            {/* Recovery Secret Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                marginBottom: '8px', 
                color: '#fff', 
                fontSize: '1.4rem',
                fontWeight: '600'
              }}>
                {t('admin.recoverySecretCode')}
              </h3>
              <p style={{ 
                marginBottom: '20px', 
                fontSize: '0.9rem', 
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.5'
              }}>
                {t('admin.recoverySecretDescription')}
              </p>

              {recoverySecretSuccess && (
                <div style={{ 
                  color: '#4facfe', 
                  fontSize: '0.9rem', 
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(79, 172, 254, 0.1)',
                  border: '1px solid rgba(79, 172, 254, 0.3)',
                  borderRadius: '8px'
                }}>
                  ✓ {t('admin.recoverySecretUpdatedSuccess')}
                </div>
              )}

              {savedRecoverySecret && (
                <div style={{ 
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ 
                      fontSize: '0.85rem', 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: '500'
                    }}>
                      {t('admin.savedRecoverySecret')}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => setShowRecoverySecret(!showRecoverySecret)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255, 255, 255, 0.6)',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {showRecoverySecret ? <FaEyeSlash /> : <FaEye />}
                        {showRecoverySecret ? t('admin.hide') : t('admin.show')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(savedRecoverySecret);
                          alert(t('admin.copied'));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255, 255, 255, 0.6)',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <FaCopy />
                        {t('admin.copy')}
                      </button>
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '1.1rem',
                    fontFamily: 'monospace',
                    color: '#fff',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    wordBreak: 'break-all',
                    letterSpacing: '2px'
                  }}>
                    {showRecoverySecret ? savedRecoverySecret : '•'.repeat(savedRecoverySecret.length)}
                  </div>
                </div>
              )}

              <form onSubmit={handleUpdateRecoverySecret}>
                {/* Hidden username field for accessibility */}
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value="admin"
                  readOnly
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                  }}
                  aria-hidden="true"
                />
                <div className="password-input-wrapper" style={{ marginBottom: '16px', position: 'relative' }}>
                  <input
                    type={showRecoverySecretPassword ? 'text' : 'password'}
                    placeholder={t('admin.currentPassword')}
                    value={recoverySecretPassword}
                    onChange={(e) => {
                      setRecoverySecretPassword(e.target.value);
                      setRecoverySecretError('');
                      setRecoverySecretFieldErrors(prev => ({ ...prev, currentPassword: '' }));
                    }}
                    className={recoverySecretFieldErrors.currentPassword ? 'password-input-error' : ''}
                    autoComplete="current-password"
                    disabled={recoverySecretLoading}
                    style={{
                      width: '100%',
                      padding: '12px 45px 12px 16px',
                      border: recoverySecretFieldErrors.currentPassword ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowRecoverySecretPassword(!showRecoverySecretPassword)}
                    className="password-toggle-btn"
                    tabIndex={-1}
                    disabled={recoverySecretLoading}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.6)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem',
                      opacity: recoverySecretLoading ? 0.5 : 1
                    }}
                  >
                    {showRecoverySecretPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {recoverySecretFieldErrors.currentPassword && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {recoverySecretFieldErrors.currentPassword}
                    </span>
                  )}
                </div>

                <div className="password-input-wrapper" style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder={t('admin.newRecoverySecret')}
                    value={recoverySecret}
                    onChange={(e) => {
                      setRecoverySecret(e.target.value);
                      setRecoverySecretError('');
                      setRecoverySecretFieldErrors(prev => ({ ...prev, secret: '', confirmSecret: '' }));
                    }}
                    className={recoverySecretFieldErrors.secret ? 'password-input-error' : ''}
                    disabled={recoverySecretLoading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: recoverySecretFieldErrors.secret ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  {recoverySecretFieldErrors.secret && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {recoverySecretFieldErrors.secret}
                    </span>
                  )}
                </div>

                <div className="password-input-wrapper" style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder={t('admin.confirmRecoverySecret')}
                    value={recoverySecretConfirm}
                    onChange={(e) => {
                      setRecoverySecretConfirm(e.target.value);
                      setRecoverySecretError('');
                      setRecoverySecretFieldErrors(prev => ({ ...prev, confirmSecret: '' }));
                    }}
                    className={recoverySecretFieldErrors.confirmSecret ? 'password-input-error' : ''}
                    disabled={recoverySecretLoading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: recoverySecretFieldErrors.confirmSecret ? '2px solid #e53e3e' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                  {recoverySecretFieldErrors.confirmSecret && (
                    <span className="password-error-message" style={{ textAlign: 'left', marginTop: '6px' }}>
                      {recoverySecretFieldErrors.confirmSecret}
                    </span>
                  )}
                </div>

                {recoverySecretError && !recoverySecretFieldErrors.currentPassword && !recoverySecretFieldErrors.secret && !recoverySecretFieldErrors.confirmSecret && (
                  <div style={{ 
                    color: '#e53e3e', 
                    fontSize: '0.85rem', 
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'rgba(229, 62, 62, 0.1)',
                    border: '1px solid rgba(229, 62, 62, 0.3)',
                    borderRadius: '8px'
                  }}>
                    {recoverySecretError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={recoverySecretLoading || recoverySecretSuccess}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: recoverySecretLoading || recoverySecretSuccess 
                      ? 'rgba(74, 85, 104, 0.5)' 
                      : 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: recoverySecretLoading || recoverySecretSuccess ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: recoverySecretLoading || recoverySecretSuccess ? 0.7 : 1,
                    boxShadow: recoverySecretLoading || recoverySecretSuccess ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!recoverySecretLoading && !recoverySecretSuccess) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(74, 85, 104, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!recoverySecretLoading && !recoverySecretSuccess) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                    }
                  }}
                >
                  {recoverySecretLoading ? t('admin.updating') : t('admin.updateRecoverySecret')}
                </button>
              </form>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Admin;

